import type { IContextMutationResult } from '@modules/core/services/query/types';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { useToastStore } from '@modules/toast/store';

import type { IPostbackChangeStatusPayload } from '../types';
import {
  ADMIN_POSTBACK_STATUS_ACTIVE_URL,
  ADMIN_POSTBACK_STATUS_INACTIVE_URL,
  AFFILIATE_POSTBACK_STATUS_ACTIVE_URL,
  AFFILIATE_POSTBACK_STATUS_INACTIVE_URL,
  POSTBACKS_LIST_QUERY_KEY,
  POSTBACKS_PERMISSIONS,
  POSTBACKS_STATUS_CHANGE_MUTATION_KEY,
} from '../constants';

function getPostbackStatusUrl(isAdminNamespace: boolean, active: boolean): string {
  if (isAdminNamespace) {
    return active
      ? ADMIN_POSTBACK_STATUS_INACTIVE_URL
      : ADMIN_POSTBACK_STATUS_ACTIVE_URL;
  }

  return active
    ? AFFILIATE_POSTBACK_STATUS_INACTIVE_URL
    : AFFILIATE_POSTBACK_STATUS_ACTIVE_URL;
}

export default function usePostbackChangeStatusMutation(): IContextMutationResult<
  void,
  Error,
  IPostbackChangeStatusPayload
> {
  const { displayPresetToast } = useToastStore();
  const { fetchPut } = useRestClient();
  const { isAdminNamespace } = useCurrentNamespace();

  return useContextMutation<void, Error, IPostbackChangeStatusPayload>({
    mutationKey: [POSTBACKS_STATUS_CHANGE_MUTATION_KEY],
    mutationFn: ({ id, postbackId, active }) => fetchPut<void>(
      getPostbackStatusUrl(isAdminNamespace.value, active),
      {},
      {
        permission: POSTBACKS_PERMISSIONS.SHOW,
        urlParams: {
          id,
          postbackId,
        },
      },
    ),
    onSuccess({ queryClient }) {
      displayPresetToast('edited');
      void queryClient.invalidateQueries({ queryKey: [POSTBACKS_LIST_QUERY_KEY] });
    },
  });
}
